/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import { StarterKit } from '@tiptap/starter-kit';
import { lowlight } from 'lowlight';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorBubbleMenu } from './editor-bubble-menu';
import { EditorToolbar } from './editor-toolbar';
import { SlashItem, SlashMenu, SlashState, detectSlash, filterSlashItems } from './slash-menu';
import './note-editor.css';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  onChange: (markdown: string) => void;
  uploadImage: (file: File) => Promise<string>;
  value: string;
};

/**
 * Pick the image files out of a paste/drop payload.
 */

function imageFiles(list: FileList | undefined | null): File[] {
  return Array.from(list ?? []).filter(file => file.type.startsWith('image/'));
}

/**
 * Export `NoteEditor` component — a WYSIWYG (Tiptap) editor whose value is
 * markdown. The initial `value` is parsed once on mount; remount (React `key`)
 * to load a different note, so autosave refetches never disturb the caret.
 */

export function NoteEditor({ className, onChange, uploadImage, value }: Props): JSX.Element {
  const [translate] = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [slash, setSlash] = useState<SlashState | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const [linkOpen, setLinkOpen] = useState(false);

  // Refs so the editor's (memoized) callbacks always see the latest values.
  const onChangeRef = useRef(onChange);
  const uploadImageRef = useRef(uploadImage);
  const slashRef = useRef<SlashState | null>(null);
  const slashItemsRef = useRef<SlashItem[]>([]);
  const slashIndexRef = useRef(0);
  const slashDismissedRef = useRef(false);
  const applySlashRef = useRef<(item: SlashItem) => void>(() => undefined);
  const editorRef = useRef<Editor | null>(null);

  onChangeRef.current = onChange;
  uploadImageRef.current = uploadImage;
  slashRef.current = slash;
  slashIndexRef.current = slashIndex;

  const insertImage = useCallback((editor: Editor, file: File, position?: number) => {
    uploadImageRef
      .current(file)
      .then(src => {
        const attrs = { alt: file.name, src };

        if (typeof position === 'number') {
          editor.chain().focus().insertContentAt(position, { attrs, type: 'image' }).run();
        } else {
          editor.chain().focus().setImage(attrs).run();
        }
      })
      .catch(() => undefined);
  }, []);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        codeBlock: false,
        heading: { levels: [1, 2, 3, 4] },
        link: { autolink: true, defaultProtocol: 'https', openOnClick: false },
        underline: false
      }),
      CodeBlockLowlight.configure({ lowlight }),
      Highlight,
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder: translate('notes.editor.placeholder') }),
      Markdown
    ],
    [translate]
  );

  const editor = useEditor({
    content: value,
    contentType: 'markdown',
    editorProps: {
      attributes: { class: 'note-editor-content' },
      handleDrop: (view, event, _slice, moved) => {
        const files = imageFiles(event.dataTransfer?.files);

        if (moved || files.length === 0) {
          return false;
        }

        event.preventDefault();

        const position = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.to;

        files.forEach(file => editorRef.current && insertImage(editorRef.current, file, position));

        return true;
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          setLinkOpen(true);

          return true;
        }

        const open = slashRef.current;

        if (!open) {
          return false;
        }

        const items = slashItemsRef.current;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setSlashIndex(current => (items.length ? (current + 1) % items.length : 0));

            return true;

          case 'ArrowUp':
            event.preventDefault();
            setSlashIndex(current => (items.length ? (current - 1 + items.length) % items.length : 0));

            return true;

          case 'Enter':
          case 'Tab': {
            const item = items[Math.min(slashIndexRef.current, items.length - 1)];

            if (!item) {
              return false;
            }

            event.preventDefault();
            applySlashRef.current(item);

            return true;
          }

          case 'Escape':
            event.preventDefault();
            slashDismissedRef.current = true;
            setSlash(null);

            return true;

          default:
            return false;
        }
      },
      handlePaste: (_view, event) => {
        const files = imageFiles(event.clipboardData?.files);

        if (files.length === 0) {
          return false;
        }

        event.preventDefault();
        files.forEach(file => editorRef.current && insertImage(editorRef.current, file));

        return true;
      }
    },
    extensions,
    onUpdate: ({ editor: current }) => {
      onChangeRef.current(current.getMarkdown());
    }
  });

  editorRef.current = editor;

  // Track the `/query` token on every transaction to open/close the slash menu.
  useEffect(() => {
    const refresh = () => {
      const next = detectSlash(editor, scrollRef.current);

      if (!next) {
        slashDismissedRef.current = false;
        setSlash(null);

        return;
      }

      if (slashDismissedRef.current) {
        return;
      }

      setSlash(current => {
        if (current?.query !== next.query) {
          setSlashIndex(0);
        }

        return next;
      });
    };

    const close = () => setSlash(null);

    editor.on('transaction', refresh);
    editor.on('blur', close);

    return () => {
      editor.off('transaction', refresh);
      editor.off('blur', close);
    };
  }, [editor]);

  const slashItems = useMemo(
    () => (slash ? filterSlashItems(slash.query, item => translate(`notes.editor.blocks.${item.key}`)) : []),
    [slash, translate]
  );

  slashItemsRef.current = slashItems;

  const pickImage = useCallback(() => fileInputRef.current?.click(), []);

  const applySlash = useCallback(
    (item: SlashItem) => {
      const open = slashRef.current;

      if (!open) {
        return;
      }

      editor.chain().focus().deleteRange({ from: open.from, to: open.to }).run();
      setSlash(null);
      item.run(editor, { pickImage });
    },
    [editor, pickImage]
  );

  applySlashRef.current = applySlash;

  const handleFilePicked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      imageFiles(event.target.files).forEach(file => insertImage(editor, file));
      event.target.value = '';
    },
    [editor, insertImage]
  );

  // Clicking the empty area under the content puts the caret at the end.
  const handleSurfaceClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget || (event.target as HTMLElement).dataset.editorSurface) {
        editor.chain().focus('end').run();
      }
    },
    [editor]
  );

  return (
    <div className={cn('note-editor flex min-h-0 flex-1 flex-col', className)}>
      <EditorToolbar editor={editor} linkOpen={linkOpen} onLinkOpenChange={setLinkOpen} onPickImage={pickImage} />

      <div className={'relative min-h-0 flex-1 overflow-y-auto'} onClick={handleSurfaceClick} ref={scrollRef}>
        <div className={'min-h-full w-full max-w-[820px] px-1 pt-5'} data-editor-surface>
          <EditorContent editor={editor} />
        </div>

        {slash && (
          <SlashMenu
            index={slashIndex}
            items={slashItems}
            onHover={setSlashIndex}
            onSelect={applySlash}
            state={slash}
          />
        )}
      </div>

      <EditorBubbleMenu editor={editor} onLink={() => setLinkOpen(true)} />

      <input accept={'image/*'} className={'hidden'} onChange={handleFilePicked} ref={fileInputRef} type={'file'} />
    </div>
  );
}
